import React from 'react';
import type { ThemeOptions } from '@mui/material/styles';
import { tokens, componentTokens } from './tokens';
import { CheckboxIcon } from '../components/ui/Checkbox/CheckboxIcon';
import { RadioIcon } from '../components/ui/Radio/RadioIcon';

/*
 * heidrick — MUI component customizations
 *
 * Per the the design system framework's new-primitive workflow + research-backed
 * extend-not-wrap pattern (Q3.2): each primitive contributes a key under
 * `components` here (MuiButton, etc.) with `variants`, `styleOverrides`, and
 * `defaultProps`. Consumers import from @mui/material directly and get the
 * Heidrick-themed version automatically.
 *
 * Tier 1 → MUI theme: Tier 1 raw values are sourced from `./tokens` (generated
 * from src/styles/tokens-raw.css via scripts/generate-theme-tokens.mjs).
 * Inlined as resolved hex strings to avoid the colorManipulator var() trap on
 * MUI 5.10.6 — see Q3.4 research.
 *
 * Tier 3 → MUI variants: per-primitive component tokens (--hd-c-<primitive>-*)
 * live in tokens-components.css and chain Tier 3 → Tier 2 → Tier 1. The
 * generator resolves the full chain and exposes resolved hex via
 * `componentTokens.<primitive>.*` for MUI variants to consume. CSS layer
 * remains canonical for audit (the token audit Check A); JS layer is a
 * mechanical mirror. Decision S248-001-002.
 */

// Pull Tier 1 hex values for terseness in variant style blocks.
const c = tokens.color;
// Pull resolved Tier 3 Button tokens (mirror of --hd-c-btn-* CSS layer).
const tb = componentTokens.btn;
// Pull resolved Tier 3 Toggle (Switch) tokens (mirror of --hd-c-toggle-* CSS layer).
const SWITCH_TK = componentTokens.toggle;

// Switch thumb mask SVGs — stroke shapes; color irrelevant (mask uses alpha only).
//   Off: two diagonals at (3,3→13,13) and (3,13→13,3), 1.5 stroke, square caps.
//   On: single polyline checkmark M 1 7 L 7 13 L 15 3, 1.5 stroke, butt caps.
// Path data verbatim from the Figma source.json.
const SWITCH_THUMB_OFF_MASK =
  "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='%23000' stroke-width='1.5' stroke-linecap='square'%3E%3Cline x1='3' y1='3' x2='13' y2='13'/%3E%3Cline x1='3' y1='13' x2='13' y2='3'/%3E%3C/svg%3E\")";
const SWITCH_THUMB_ON_MASK =
  "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='%23000' stroke-width='1.5'%3E%3Cpath d='M 1 7 L 7 13 L 15 3'/%3E%3C/svg%3E\")";

/*
 * Avatar variant matrix — 4 sizes × 6 color tones = 24 cells. Rather than
 * enumerate 24 near-identical objects (where adding a size means editing 6
 * places), the per-size dimensions and per-tone colors are factored into two
 * tables and the variants array is generated by their cross-product. Change a
 * size once in AVATAR_DIMS → all 6 of its tones update. Mirrors the DRY
 * discipline of the Button helpers above.
 */
type AvatarSize = 'xs' | 's' | 'l' | 'xl';
type AvatarTone =
  | 'neutral'
  | 'bright-blue'
  | 'bright-teal'
  | 'teal'
  | 'darker-blue'
  | 'teal-active';

const AVATAR_DIMS: Record<
  AvatarSize,
  {
    width: number; height: number; padding: number; borderRadius: number;
    fontSize: number; fontWeight: number; lineHeight: string;
    // The neutral tone is the only one with a visible border; its width grows
    // from 1px (xs/s) to 2px (l/xl) per Figma source.
    neutralBorderWidth: number;
  }
> = {
  xs: { width: 26, height: 26, padding: 4, borderRadius: 40, fontSize: 14, fontWeight: 400, lineHeight: '26px', neutralBorderWidth: 1 },
  s: { width: 40, height: 40, padding: 4, borderRadius: 40, fontSize: 16, fontWeight: 400, lineHeight: '24px', neutralBorderWidth: 1 },
  l: { width: 100, height: 100, padding: 22, borderRadius: 50, fontSize: 28, fontWeight: 200, lineHeight: '34px', neutralBorderWidth: 2 },
  xl: { width: 120, height: 120, padding: 22, borderRadius: 80, fontSize: 38, fontWeight: 200, lineHeight: '42px', neutralBorderWidth: 2 },
};

const AVATAR_TONES: Record<AvatarTone, { bg: string; fg: string }> = {
  'neutral': { bg: componentTokens.avatar.neutralBg, fg: componentTokens.avatar.neutralFg },
  'bright-blue': { bg: componentTokens.avatar.brightBlueBg, fg: componentTokens.avatar.brightBlueFg },
  'bright-teal': { bg: componentTokens.avatar.brightTealBg, fg: componentTokens.avatar.brightTealFg },
  'teal': { bg: componentTokens.avatar.tealBg, fg: componentTokens.avatar.tealFg },
  'darker-blue': { bg: componentTokens.avatar.darkerBlueBg, fg: componentTokens.avatar.darkerBlueFg },
  'teal-active': { bg: componentTokens.avatar.tealActiveBg, fg: componentTokens.avatar.tealActiveFg },
};

const AVATAR_VARIANTS = (Object.keys(AVATAR_DIMS) as AvatarSize[]).flatMap((size) => {
  const dim = AVATAR_DIMS[size];
  return (Object.keys(AVATAR_TONES) as AvatarTone[]).map((tone) => {
    const color = AVATAR_TONES[tone];
    return {
      props: { size, variant: tone },
      style: {
        width: dim.width,
        height: dim.height,
        padding: dim.padding,
        borderRadius: dim.borderRadius,
        backgroundColor: color.bg,
        color: color.fg,
        fontSize: dim.fontSize,
        fontWeight: dim.fontWeight,
        lineHeight: dim.lineHeight,
        // Neutral is the only tone with a visible border (Figma source).
        ...(tone === 'neutral' && {
          border: `${dim.neutralBorderWidth}px solid ${color.bg}`,
        }),
      },
    };
  });
});

/*
 * Button DRY helpers — AC #1. Each state block defined ONCE;
 * per-cell values flow in as arguments. Change a helper's shape → cascades to
 * all 8 Button variants. (Helpers are per-primitive; Avatar etc. have their
 * own state shapes — share the framework discipline, not the helper code.)
 */

/*
 * Focus-visible — same-color outer ring + 4px transparent moat per Figma source.
 *
 * Pixel-level analysis of primary--arrow--focus--m--dark.png (124x40):
 *   x=0: 1px outer ring (color = bg color, e.g., darker-blue for M Dark)
 *   x=1-3: 3px transparent (the "moat" — shows page bg through)
 *   x=4-119: inner pill bg fill (e.g., darker-blue)
 *   mirror on other side
 *
 * The outer ring is the SAME COLOR as the button's bg. Against the button
 * itself the ring is invisible; against the surrounding page bg, the
 * combination of (transparent moat + bg-colored ring) reads as a subtle halo.
 *
 * Earlier mistake (caught by user feedback "M and S are intermingled"): used
 * the bright-teal focus token for the ring color. That produced a prominent
 * vibrant ring + visible shrunken inner pill — looked like an S-size pill
 * inside an M-size outer perimeter. Wrong color, even though structure was
 * close.
 *
 * CSS implementation (no DOM restructure):
 *   border: 4px solid transparent → 4px moat inside outer edge
 *   backgroundClip: padding-box → bg only fills inside the border (pixel 4+)
 *   boxShadow: inset 1px <ringColor> → 1px ring at pixel 0 (outer perimeter)
 *   padding: <default minus 4 each side> → keeps content position invariant
 *
 * Matches `:focus-visible` (real focus), `.Mui-focusVisible` (MUI class), and
 * `[data-force-focus]` (catalog matrix static render).
 *
 * `basePadding` optional — Icon variants don't have padding to reduce, content
 * just shrinks slightly inside the moat.
 */
/**
 * Shrink each side of a CSS-padding shorthand by `gap` pixels and emit the
 * explicit 4-value form. Used by `focusRing` to keep content position invariant
 * when 4px of padding is consumed by the focus-state moat.
 *
 * Accepts the standard 1/2/3/4-value padding shorthand:
 *   `"8px"` → top=right=bottom=left=8
 *   `"8px 16px"` → top=bottom=8, right=left=16
 *   `"4px 8px 6px"` → top=4, right=left=8, bottom=6
 *   `"4px 6px 8px 10px"` → top=4, right=6, bottom=8, left=10
 *
 * @param basePadding - CSS padding shorthand (1/2/3/4-value form)
 * @param gap - pixels to subtract from each side
 * @returns explicit 4-value padding string (`"Tpx Rpx Bpx Lpx"`)
 */
function shrinkPad(basePadding: string, gap: number): string {
  const parts = basePadding.trim().split(/\s+/).map((s) => parseInt(s, 10));
  let t: number, r: number, b: number, l: number;
  if (parts.length === 1) { t = r = b = l = parts[0]; }
  else if (parts.length === 2) { t = b = parts[0]; r = l = parts[1]; }
  else if (parts.length === 3) { t = parts[0]; r = l = parts[1]; b = parts[2]; }
  else { t = parts[0]; r = parts[1]; b = parts[2]; l = parts[3]; }
  return `${t - gap}px ${r - gap}px ${b - gap}px ${l - gap}px`;
}
/**
 * Build a `:focus-visible` style block matching Figma's nested-frame ring +
 * 4px transparent moat. See the section comment above for the pixel-level
 * rendering rationale and the boxShadow-vs-outline trade-off.
 *
 * The style block also matches `.Mui-focusVisible` (MUI's class) and the
 * `[data-force-focus]` attribute (catalog static render).
 *
 * @param ringColor - outer 1px ring color (typically the button's own bg)
 * @param _width - reserved for a future 2px-ring variant; currently unused
 * @param _offset - reserved for a future variant; currently unused
 * @param extra - additional CSS merged into the focus block. If `basePadding`
 *                is provided, it's shrunk by 4px each side via `shrinkPad` so
 *                content position stays invariant across default ↔ focus.
 * @returns MUI sx-shape with the `:focus-visible /.Mui-focusVisible /
 *          [data-force-focus]` rule populated.
 */
const focusRing = (ringColor: string, _width: 1 | 2 = 1, _offset = 4, extra: { basePadding?: string } & Record<string, unknown> = {}) => {
  const { basePadding,...rest } = extra;
  const gap = 4;
  // Rendering subtlety: `boxShadow inset 1px` paints at the PADDING-BOX edge
  // (inner side of border), NOT at the outer box perimeter. With border:4px
  // transparent, that puts the ring at pixel 4 — losing the visible outer
  // perimeter ring Figma shows. Using `outline` with `outlineOffset: -1px`
  // instead paints the ring AT pixel 0 (outer box edge). Outline draws on top
  // of the transparent border, producing the visible 1px ring at the outer
  // perimeter that Figma's PNG shows.
  return {
    '&:focus-visible, &.Mui-focusVisible, &[data-force-focus]': {
      border: `${gap}px solid transparent`,
      backgroundClip: 'padding-box',
      outline: `1px solid ${ringColor}`,
      outlineOffset: '-1px',
      boxShadow: 'none',
      ...(basePadding ? { padding: shrinkPad(basePadding, gap) } : {}),...rest,
    },
  };
};

// Disabled — three shapes (Primary filled, Secondary outlined, Icon-only). Structure shared per shape.

/**
 * Disabled-state rule for Primary (filled) variants: muted bg + muted fg.
 * @param bg - disabled background color
 * @param fg - disabled foreground (text/icon) color
 */
const disabledPrimary = (bg: string, fg: string) => ({
  '&.Mui-disabled': { backgroundColor: bg, color: fg },
});

/**
 * Disabled-state rule for Secondary (outlined) variants: 1px muted border + muted fg.
 * @param border - disabled border color
 * @param fg - disabled foreground (text/icon) color
 */
const disabledSecondary = (border: string, fg: string) => ({
  '&.Mui-disabled': { border: `1px solid ${border}`, color: fg },
});

/**
 * Hover / active emphasis for Secondary (outlined) variants. The ring reads as
 * ~1.5px on hover, but the border stays a constant 1px — the extra 0.5px is
 * painted with an inset box-shadow, which is layout-neutral. Animating
 * `border-width` (the previous approach) grew the border-box on an auto-width
 * button, and since the button's left edge is pinned by layout the label got
 * pushed ~0.5px sideways on hover. A box-shadow can't shift anything.
 * Factored out so all four secondary blocks (md/sm × dark/bright) share one
 * definition rather than repeating the treatment. Background stays transparent.
 * (The focus ring sets `boxShadow: none`, so focus wins cleanly over hover.)
 * @param color - the ring colour (matches the resting 1px border)
 */
const secondaryHover = (color: string) => ({
  '&:hover, &:active, &[data-force-hover]': {
    boxShadow: `inset 0 0 0 0.5px ${color}`,
    backgroundColor: 'transparent',
  },
});

// `disabledIcon` helper was authored alongside disabledPrimary / disabledSecondary
// but never consumed (Icon variant disabled state uses inline style instead).
// Removed 2026-06-01 to clear the noUnusedLocals warning. If the Icon variant
// migrates to MUI theme overrides in the future, recreate per the same shape
// as disabledPrimary / disabledSecondary above.

/*
 * Focus-visible — Figma's Focus variant uses a nested-frame structure:
 *   outer COMPONENT: 124x40, padding 4px, border 1px solid (variant color), radius 100px
 *   inner FRAME: 116x32, padding 4px 21px, background (variant bg), radius 100px
 *
 * The 4px padding on the outer creates a gap between the inner pill and the 1px
 * ring border. CSS-equivalent: shrink the button (inner padding) + outline at
 * 4px offset (outer ring). Plus suppress MUI's default focus-visible overlay
 * (boxShadow: 'none') so it doesn't double up.
 *
 * The outline color matches the variant's background by Figma spec — visible only
 * when surrounding context contrasts. Keyboard-focus indicator at the design's
 * intent, not CSS-default outline.
 */

export const heidrickComponents: ThemeOptions['components'] = {
  // ────────────────────────────────────────────────────────────────────────────
  // Button — CATALOG
  // 8 variant entries (Style × Size × Tone); states inline via MUI selectors.
  // Type axis (Arrow/Close/Download/Text/Text only/Icon) maps to MUI's
  // composition pattern (startIcon/endIcon/children) — not a separate variant.
  // ────────────────────────────────────────────────────────────────────────────
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      disableRipple: true, // Heidrick uses subtle Figma-spec hover; no Material ripple/focus overlay
    },
    styleOverrides: {
      root: {
        // Universal across all Heidrick Button variants per Figma source:
        fontFamily: tokens.font['family-avenir'],
        fontSize: tokens.font['size-16'],
        fontWeight: 300, // Avenir Light — matches Figma rendering (verified 2026-05-19 via the dimension verification process: 300/350 both produce 123.98px vs Figma baseline 124, weight 400 was 0.64px wider). Figma's CATALOG named the style "Avenir 16 Book" but the actual rendered weight matches Light.
        lineHeight: tokens.font['line-height-16'],
        textTransform: 'none', // Heidrick doesn't uppercase button text
        whiteSpace: 'nowrap', // Heidrick buttons are pill-shaped — label never wraps to a second line. Long labels (e.g. "Previous Question") get clipped or grow the button rather than producing a two-line oval. Caught when V4's question screen sized a secondary button at width 180 (~130px content area) — "Previous Question" wrapped to two lines and the pill stretched vertically.
        borderRadius: 100, // pill — matches Figma's '100px' across all variants
        gap: 0, // Figma renders text + icon-container at gap=0 (frame "Button" 74w = text 48 + icon-container 26)

        // Smooth default ↔ hover animation. Animates the layout shift (padding
        // shrink + endIcon container grow + alignment shift) so the chevron tip
        // glides rightward instead of jumping. cubic-bezier matches MUI's standard
        // ease-out for consistency with other Material transitions.
        transition: 'padding 200ms cubic-bezier(0.4, 0, 0.2, 1), background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',

        // Generic icon container (Icon.Standard for close, download, etc.) — keeps total
        // button width invariant at 124 by giving the icon a fixed 26×24 frame. Without
        // this override MUI's default `.MuiButton-endIcon { marginLeft: 8, marginRight: -4 }`
        // adds a net +4 to button width, producing 126x40 instead of 124x40.
        // BUG FIX 2026-05-19 (follow-up): primary--close and primary--download
        // were rendering at 126px because they don't trigger the `:has(.hd-btn-arrow)`
        // selector below. This generic rule covers them; the `:has` rule still wins via
        // specificity for ButtonArrow's specific overflow + flex-start layout.
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
          marginLeft: 0,
          marginRight: 0,
          width: 26,
          height: 24,
          boxSizing: 'border-box',
          padding: '0 1px', // center 24px Icon.Standard in 26px frame
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },

        // ButtonArrow handling: applies to ANY Heidrick Button rendering <ButtonArrow />
        // in endIcon. :has() selector scopes the overrides to that case only —
        // Buttons using Icon.Standard (close, download, etc.) keep MUI's defaults
        // (until per-icon Button-internal icon assets land).
        // Heidrick Button endIcon container — 26 wide default, animates to 30 on hover
        // (paired with button right-padding 25→21 to keep button width invariant at 124).
        '& .MuiButton-endIcon:has(.hd-btn-arrow)': {
          margin: 0,
          padding: '0 6px 0 8px',
          width: 26,
          height: 24,
          // BUG FIX 2026-05-19 (follow-up): MUI's icon span defaults to
          // box-sizing: content-box, which made `width: 26` mean "content width 26 +
          // padding 14 = 40 outer". That added 14px to total button width (124→138).
          // Forcing border-box makes width: 26 the OUTER width as intended.
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0,
          overflow: 'visible',
          transition: 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        // M only: container grows 26→30 on hover (paired with chassis right-padding 25→21).
        // S uses gap-widening instead (handled in S variant chassis: gap 2→6 on hover).
        // `[data-force-hover]` is the catalog-matrix at-rest path (Checkbox/Switch precedent).
        '&.MuiButton-sizeMd:hover .MuiButton-endIcon:has(.hd-btn-arrow), &.MuiButton-sizeMd:active .MuiButton-endIcon:has(.hd-btn-arrow), &.MuiButton-sizeMd[data-force-hover] .MuiButton-endIcon:has(.hd-btn-arrow)': {
          width: 30,
        },
        // SVG itself translates +5px right on hover. This positions the arrow's left
        // endpoint at button-x=86 in hover (vs 81 default), so chevron tip lands at
        // button-x=102 (matching Figma exactly): 86 + 16 (scaled shaft) = 102.
        '& .hd-btn-arrow': {
          transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover .hd-btn-arrow, &:active .hd-btn-arrow, &[data-force-hover] .hd-btn-arrow': {
          transform: 'translateX(6px)', // 5px → 6px to land chevron tip at button-x=102 (matches Figma exactly)
        },
        // Plus internal shaft + chevron growth at the same rate, so the arrow looks
        // like it's being PULLED rightward (shaft extending, chevron riding the tip).
        '& .hd-btn-arrow__shaft': {
          transformOrigin: '0 12px',
          transformBox: 'fill-box',
          transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '& .hd-btn-arrow__tip': {
          transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover .hd-btn-arrow__shaft, &:active .hd-btn-arrow__shaft, &[data-force-hover] .hd-btn-arrow__shaft': {
          transform: 'scaleX(1.3333)',
        },
        '&:hover .hd-btn-arrow__tip, &:active .hd-btn-arrow__tip, &[data-force-hover] .hd-btn-arrow__tip': {
          transform: 'translateX(3.54px)',
        },

        // ButtonClose (X) — Figma source: each diagonal tilts from ±45° toward
        // horizontal on hover. Two distinct scales per parent variant:
        //   - Primary close: ±45° → ±12° (nearly horizontal) → scale(1.38, 0.29)
        //   - Icon variant close: ±45° → ±38° (subtle pinch) → scale(1.115, 0.87)
        // Primary's aggressive scale is the universal default below; Icon-variant
        // blocks override with their own scale. Both share the transition + origin.
        // BUG FIX 2026-05-20: prior scale(1.186) uniform was wrong per Figma audit.
        '& .hd-btn-close': {
          transformOrigin: 'center',
          transformBox: 'fill-box',
          transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover .hd-btn-close, &:active .hd-btn-close, &[data-force-hover] .hd-btn-close': {
          transform: 'scale(1.38, 0.29)', // Primary close — Figma ±45°→±12°
        },

        // ButtonDownload (arrow + shelf) — Figma source: ARROW (shaft + chevron) moves DOWN
        // ~2px on hover; shelf stays static. Visual: arrow "descends into the tray" — the
        // download gesture. Gap between chevron tip and shelf shrinks from default to hover.
        //  follow-up 2026-05-19: created ButtonDownload component to enable
        // this; user clarified direction (arrow moves, not shelf — flipped from initial guess).
        '& .hd-btn-download__arrow': {
          transformOrigin: 'center',
          transformBox: 'fill-box',
          transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover .hd-btn-download__arrow, &:active .hd-btn-download__arrow, &[data-force-hover] .hd-btn-download__arrow': {
          transform: 'translateY(2px)',
        },
      },
    },
    variants: [
      // ──── Primary / M / Dark ──────────────────────────────────────────────
      // Chassis only — endIcon arrow handling lives in styleOverrides.root above
      // and applies to all Heidrick Buttons via :has(.hd-btn-arrow).
      {
        props: { variant: 'primary', size: 'md', tone: 'dark' },
        style: {
          padding: '8px 25px',
          backgroundColor: tb.primaryDarkBg,
          color: tb.primaryDarkFg,
          '&:hover, &:active, &[data-force-hover]': {
            backgroundColor: tb.primaryDarkBg,
            // Right padding 25→21 ONLY for arrow variants — the 4px the endIcon container
            // reclaims when ButtonArrow grows 26→30. For close/download/text (Icon.Standard
            // children or no icon), endIcon container stays 26, so reducing right padding
            // would narrow the button by 4 on hover, which Figma doesn't do.
            // BUG FIX 2026-05-19 (follow-up): scoped to :has(.hd-btn-arrow).
            '&:has(.hd-btn-arrow)': {
              padding: '8px 21px 8px 25px',
            },
          },
          // Focus — A11Y-FIRST DEVIATION FROM FIGMA. CSS outline outside the button
          // (visible ring for keyboard users) vs Figma's invisible same-color inner border.
          // Pixel-diff against Figma Focus PNG ~15% deviation entirely from this design
          // intent vs A11Y conflict. Documented trade-off.
          // Ring color = bg color (darker-blue), per Figma pixel-sample of focus PNG.
          // The bright-teal focus-ring-dark token is misleading; Figma actually
          // uses bg color, creating a halo-against-page-bg effect rather than a
          // vibrant ring.
          ...focusRing(tb.primaryDarkBg, 1, 4, { basePadding: '8px 25px' }),...disabledPrimary(tb.disabledBg, tb.disabledFg),
        },
      },
      // ──── Primary / M / Bright ────────────────────────────────────────────
      {
        props: { variant: 'primary', size: 'md', tone: 'bright' },
        style: {
          padding: '8px 25px',
          backgroundColor: tb.primaryBrightBg,
          color: tb.primaryBrightFg,
          '&:hover, &:active, &[data-force-hover]': {
            backgroundColor: tb.primaryBrightBg,
            // Same arrow-specific padding scoping as Primary M Dark above.
            '&:has(.hd-btn-arrow)': {
              padding: '8px 21px 8px 25px',
            },
          },...focusRing(tb.primaryBrightFocusRing, 1, 4, { basePadding: '8px 25px' }),...disabledPrimary(tb.primaryBrightBg, tb.disabledFgBright),
        },
      },
      // ──── Primary / S / Dark ──────────────────────────────────────────────
      {
        props: { variant: 'primary', size: 'sm', tone: 'dark' },
        style: {
          padding: '6px 8px 6px 12px',
          backgroundColor: tb.primaryDarkBg,
          color: tb.primaryDarkFg,
          // BUG FIX 2026-05-19 (follow-up): text-only variant uses symmetric
          // 23px horizontal padding per Figma `primary--text--default--s--dark` (`padding: 6px 23px`).
          // The base 6px 8px 6px 12px asymmetric padding is for variants WITH an icon
          // (arrow/close/download). When no icon is present (no startIcon/endIcon children),
          // apply symmetric padding so the text centers correctly.
          '&:not(:has(.MuiButton-startIcon, .MuiButton-endIcon))': {
            padding: '6px 23px',
          },
          // S text uses same 16px Avenir as M per Figma JSON (S "Begin" 42 vs M "Button" 48 width = shorter word, not smaller font).
          '&:hover, &:active, &[data-force-hover]': {
            // BUG FIX 2026-05-19 (follow-up): removed `gap: 6` on hover.
            // The arrow already animates via root-level `.hd-btn-arrow { transform: translateX(6px) }`
            // with overflow:visible — adding `gap: 6` here double-compensated and grew button
            // width 88 → 94. Figma baseline is 88x36 in both default AND hover-active for S.
            backgroundColor: tb.primaryDarkBg,
          },
          // Primary S Dark uses darker-blue ring per Figma (different from M Dark which uses bright-teal).
          ...focusRing(tb.primaryDarkBg, 1, 4, { basePadding: '6px 8px 6px 12px' }),
          // Text-only focus padding override: when no icon, default padding is
          // '6px 23px' (not '6 8 6 12'). Focus padding = '6 23' reduced by 4 = '2 19'.
          '&:focus-visible:not(:has(.MuiButton-startIcon, .MuiButton-endIcon)), &.Mui-focusVisible:not(:has(.MuiButton-startIcon, .MuiButton-endIcon)), &[data-force-focus]:not(:has(.MuiButton-startIcon, .MuiButton-endIcon))': {
            padding: '2px 19px',
          },...disabledPrimary(tb.disabledBg, tb.disabledFg),
        },
      },
      // ──── Primary / S / Bright ────────────────────────────────────────────
      {
        props: { variant: 'primary', size: 'sm', tone: 'bright' },
        style: {
          padding: '6px 8px 6px 12px',
          backgroundColor: tb.primaryBrightBg,
          color: tb.primaryBrightFg,
          // 16px inherited from styleOverrides.root (S text matches M per Figma)
          '&:hover, &:active, &[data-force-hover]': {
            // BUG FIX 2026-05-19 (follow-up): see Primary S Dark for the rationale.
            backgroundColor: tb.primaryBrightBg,
          },...focusRing(tb.primaryBrightFocusRing, 1, 4, { basePadding: '6px 8px 6px 12px' }),...disabledPrimary(tb.primaryBrightBg, tb.disabledFgBright),
        },
      },
      // ──── Secondary / M ───────────────────────────────────────────────────
      // Outlined style — transparent bg, 1px border, text-color matches border.
      // Figma locks explicit width: 98px / height: 40px on this variant.
      // Tone is intentionally NOT part of the matcher: this block is the
      // catch-all (the dark tone — darker-blue outline for light surfaces). It
      // owns the geometry for ALL secondary tones; the bright tone (below) is a
      // color-only overlay layered on top when tone="bright". Keeping geometry
      // un-gated means consumers who omit `tone` still hit the locked 98×40 (an
      // earlier tone="dark" matcher dropped them to MUI's base padding +
      // content-height → a 38px-tall button). Same applies to Secondary / S.
      {
        props: { variant: 'secondary', size: 'md' },
        style: {
          // `minWidth` instead of `width` so longer labels grow the button
          // (the Figma 98px is for the "Back" example — real consumers use
          // labels like "Previous Question" which need ~180px). Short labels
          // still hit the locked 98 floor for visual consistency. `fullWidth`
          // still wins (100% > 98 floor).
          minWidth: 98,
          height: 40,
          // Optical vertical centring: this outlined variant renders its label
          // ~1px higher than the Figma reference (the browser positions the
          // Avenir line box slightly high; the filled variant reads fine but the
          // outline's border ring makes it visible). Asymmetric padding (8 top /
          // 6 bottom) shifts the content — label + any icon together — down ~1px
          // onto the Figma centre. Same optical-nudge family as the numbered
          // circles. Content stays 24px (= line-height).
          padding: '8px 25px 6px',
          backgroundColor: 'transparent',
          color: tb.secondaryFg,
          border: `1px solid ${tb.secondaryBorder}`,
          boxSizing: 'border-box', // 98×40 OUTER includes 1px border on each side
          ...secondaryHover(tb.secondaryBorder),
          // Secondary focus: two concentric rings per Figma (outer at default 1px
          // border, inner 1px ring at 4px inset). Outer is the existing border;
          // inner via outline w/ negative offset (-5 = 5px inside border-edge).
          '&:focus-visible, &.Mui-focusVisible, &[data-force-focus]': {
            outline: `1px solid ${tb.secondaryFocusRing}`,
            outlineOffset: '-5px',
            boxShadow: 'none',
          },...disabledSecondary(tb.disabledBorder, tb.disabledFg),
        },
      },
      // ──── Secondary / S ───────────────────────────────────────────────────
      // Figma: outer 94×36 with 1px border (border-box). Same "tone omitted
      // from matcher" rationale as Secondary / M above — secondary is
      // dark-only in V1.
      {
        props: { variant: 'secondary', size: 'sm' },
        style: {
          // See Secondary / M above — `minWidth` so longer labels grow.
          minWidth: 94,
          height: 36,
          // Optical vertical centring (same family as Secondary / M): the 1px
          // border leaves a 22px content area inside the 36px height, 2px short
          // of the 24px line-height, so the label rides ~1px high. Trimming the
          // bottom padding 6→4 restores content = line-height and drops the line
          // box 1px onto the Figma centre (matches the filled S primary).
          padding: '6px 8px 4px 12px',
          backgroundColor: 'transparent',
          color: tb.secondaryFg,
          border: `1px solid ${tb.secondaryBorder}`,
          boxSizing: 'border-box',
          // S text uses same 16px as M per Figma
          ...secondaryHover(tb.secondaryBorder),
          // Secondary S Dark focus — same nested-rings approach as Secondary M Dark.
          '&:focus-visible, &.Mui-focusVisible, &[data-force-focus]': {
            outline: `1px solid ${tb.secondaryFocusRing}`,
            outlineOffset: '-5px',
            boxShadow: 'none',
          },...disabledSecondary(tb.disabledBorder, tb.disabledFg),
        },
      },
      // ──── Secondary / Bright (M + S) — white-on-dark overlay ───────────────
      // The two `secondary` blocks above are the catch-all (effectively the dark
      // tone — darker-blue outline for light surfaces). These bright blocks are a
      // COLOR-ONLY overlay applied when tone="bright": geometry (minWidth/height/
      // padding/transparent bg/border-box) is inherited from the catch-all block;
      // only fg + border + hover-border + focus ring + disabled flip to the
      // white-on-dark treatment so the button reads on a dark assessment surface.
      // No Figma button-component source for this tone — values are design-approved
      // (white text, white@85% border, 1.5px white on hover); focus + disabled
      // reuse the existing bright-surface tokens.
      {
        props: { variant: 'secondary', size: 'md', tone: 'bright' },
        style: {
          color: tb.secondaryBrightFg,
          border: `1px solid ${tb.secondaryBrightBorder}`,
          ...secondaryHover(tb.secondaryBrightBorder),
          '&:focus-visible, &.Mui-focusVisible, &[data-force-focus]': {
            outline: `1px solid ${tb.secondaryBrightFocusRing}`,
            outlineOffset: '-5px',
            boxShadow: 'none',
          },...disabledSecondary(tb.secondaryBrightDisabled, tb.secondaryBrightDisabled),
        },
      },
      {
        props: { variant: 'secondary', size: 'sm', tone: 'bright' },
        style: {
          color: tb.secondaryBrightFg,
          border: `1px solid ${tb.secondaryBrightBorder}`,
          ...secondaryHover(tb.secondaryBrightBorder),
          '&:focus-visible, &.Mui-focusVisible, &[data-force-focus]': {
            outline: `1px solid ${tb.secondaryBrightFocusRing}`,
            outlineOffset: '-5px',
            boxShadow: 'none',
          },...disabledSecondary(tb.secondaryBrightDisabled, tb.secondaryBrightDisabled),
        },
      },
      // ──── Icon / M / Bright ───────────────────────────────────────────────
      // 42x42 circular WHITE button with DARKER-BLUE icon — designed to stand out
      // on dark surfaces. Per Figma source (icon--close--default--m--bright.json):
      //   - Ellipse 4 (42x42) fill: WHITE (#FFF)
      //   - X stroke: DARKER-BLUE (#002136)
      //   - Disabled: bg → gray-3-5 (#CCC), stroke → gray-2 (#878787)
      //   - Focus: 42x42 outer ring (darker-blue stroke) + 34x34 inner white fill
      // BUG FIX 2026-05-19 (follow-up): bg was 'transparent' + color was
      // white → invisible button + invisible icon. User screenshot caught this.
      {
        props: { variant: 'icon', size: 'md', tone: 'bright' },
        style: {
          width: 42,
          height: 42,
          padding: 0,
          minWidth: 0, // override MUI's default Button min-width
          backgroundColor: c.white,
          color: c['darker-blue'],
          // Reset the generic 26x24.MuiButton-startIcon override (set at root for
          // Primary/Secondary). Icon-variant buttons need the icon to flow naturally
          // inside the 42x42 frame, not be constrained to 26x24.
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            margin: 0,
            padding: 0,
            width: 'auto',
            height: 'auto',
          },
          // Override Primary's `.hd-btn-close` hover scale for Icon variant:
          // Figma's Icon-variant close hover is a subtler pinch (±45° → ±38° =
          // scale 1.115, 0.87) vs Primary's aggressive ±45° → ±12° (1.38, 0.29).
          '&:hover .hd-btn-close, &:active .hd-btn-close, &[data-force-hover] .hd-btn-close': {
            transform: 'scale(1.115, 0.87)',
          },
          '&:hover, &:active, &[data-force-hover]': {
            backgroundColor: c.white,
          },
          // Focus: Figma's nested-frame approach (outer 42x42 stroke + inner 34x34
          // shrink) approximated via CSS outline OUTSIDE the button — keeps a11y
          // visibility without restructuring the DOM. The visual "circle around it"
          // the user expected is satisfied by the outline.
          ...focusRing(c['darker-blue'], 1, 2),
          // Disabled: bg lightens to gray-3-5, icon to gray-2.
          '&.Mui-disabled': { backgroundColor: c['gray-3-5'], color: c['gray-2'] },
        },
      },
      // ──── Icon / S / Bright ───────────────────────────────────────────────
      // 32x32 circular WHITE button — same color treatment as Icon M Bright above.
      // Per Figma source. BUG FIX 2026-05-19 (follow-up): same inversion
      // bug as Icon M Bright; bg was transparent + color white.
      {
        props: { variant: 'icon', size: 'sm', tone: 'bright' },
        style: {
          width: 32,
          height: 32,
          padding: 4,
          gap: 0,
          minWidth: 0,
          borderRadius: 40,
          boxSizing: 'border-box',
          backgroundColor: c.white,
          color: c['darker-blue'],
          // Reset generic 26x24 startIcon override — let icon flow inside the 32x32
          // frame (4px padding → 24x24 inner area for a sm/16px icon).
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            margin: 0,
            padding: 0,
            width: 'auto',
            height: 'auto',
          },
          // Hover bg: Figma overlays rgba(100,106,124,0.20) on the white circle —
          // produces a subtle grey-tint. Pre-blended hex: rgb(224, 225, 229).
          // Glyph itself does not move/scale on hover per Figma.
          transition: 'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover, &:active, &[data-force-hover]': {
            backgroundColor: 'rgb(224, 225, 229)',
          },...focusRing(c['darker-blue'], 1, 2),
          '&.Mui-disabled': { backgroundColor: c['gray-3-5'], color: c['gray-2'] },
        },
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────────
  // Avatar — CATALOG
  // 24 variant cells from Figma matrix Size {XS,S,L,XL} × Type {Neutral,
  // Bright Blue, Bright Teal, Teal, Darker Blue, Teal Active}.
  //
  // Implementation: variants array splits Type axis (color tokens) from Size
  // axis (dimensional literals). Sizes XS/XL are explicit in Figma JSON; S/L
  // are computed from text-box + padding (S = 32 + 4*2 = 40; L = 56 + 22*2 = 100).
  //
  // Neutral type adds a 1-2px border in the same gray-400 as bg per Figma source
  // (visually invisible against the bg, present for legacy DOM consistency).
  // Other Types render with no border.
  //
  // Font weight 350 on L/XL matches Figma's resolved CSS weight — browsers snap
  // to nearest available Avenir glyph (same trade-off Button accepts).
  //
  // Note from Figma source: Size axis includes a stray "Teal Active" variant
  // cell (`Size=Teal Active, Type=Teal Active`) which is a Figma authoring bug.
  // CATALOG locks this cell as `Size=S, Type=Teal Active` — the value matches.
  // Flag for designer to rename in Figma.
  // ────────────────────────────────────────────────────────────────────────────
  MuiAvatar: {
    defaultProps: {
      variant: 'neutral' as const,
      // MUI's default variant is 'circular' (string union); our augmentation
      // adds the Heidrick variants but doesn't remove the MUI defaults — so
      // 'circular'/'rounded'/'square' still work, just unstyled by Heidrick.
    },
    styleOverrides: {
      root: {
        // Universal across all Heidrick Avatar variants per Figma source:
        fontFamily: tokens.font['family-avenir'],
        textAlign: 'center',
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // MUI's Avatar default applies overflow:hidden and a background image
        // pattern that conflicts with our text-only initials. Clear them.
        overflow: 'hidden',
      },
    },
    // 24 cells (4 sizes × 6 tones) generated from AVATAR_DIMS × AVATAR_TONES.
    variants: AVATAR_VARIANTS,
  },

  // ────────────────────────────────────────────────────────────────────────────
  // Checkbox — CATALOG
  // 10 variant cells from Figma matrix States {Default, Hover, Disabled, Error,
  // Focus} × On/Off {False, True}.
  //
  // MUI mapping:
  //   - Native `checked` prop carries Figma's On/Off axis (True ↔ checked).
  //   - Pseudo-classes carry the States axis: `:hover`, `:focus-visible`,
  //     `.Mui-disabled`, plus a custom `error` prop for Error state.
  //   - Custom SVG icon (CheckboxIcon.tsx) replaces MUI's default checked/unchecked
  //     glyphs via defaultProps; styling drives chassis (border, bg) + glyph color.
  //
  // Figma source dimensions: 24×24 chassis, 4px border-radius, 15×10.41 checkmark.
  // Focus state nests an outer 28×28 ring (1px Darker-Blue, 4px radius) outside
  // the 24×24 chassis (radius drops to 3px). CSS-equivalent: outline at +2 offset.
  // ────────────────────────────────────────────────────────────────────────────
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: React.createElement(CheckboxIcon),
      checkedIcon: React.createElement(CheckboxIcon, { checked: true }),
    },
    styleOverrides: {
      root: {
        // MUI Checkbox wraps the icon in a button-like span with built-in
        // padding for the ripple. Reset to render exactly the 24×24 chassis +
        // 4px buffer for the focus-visible 28×28 ring offset.
        padding: 0,
        // Universal chassis styling lives on the.hd-checkbox-icon child span.
        // Border stroke painted via inset box-shadow so the 24×24 content area
        // is invariant to stroke weight (Figma stroke alignment behavior). A
        // CSS `border` would push the content-area inward and shift the glyph
        // by the border width.
        '& .hd-checkbox-icon': {
          position: 'relative',
          width: 24,
          height: 24,
          borderRadius: 4,
          boxSizing: 'border-box',
          backgroundColor: componentTokens.checkbox.bgDefault,
          boxShadow: `inset 0 0 0 1px ${componentTokens.checkbox.borderDefault}`,
          color: componentTokens.checkbox.checkFg,
          display: 'inline-block',
          transition: 'box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background-color 120ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        // Anchor the checkmark glyph at Figma's authoritative (x=4, y=7)
        // origin inside the 24×24 chassis. Flex-centering produced half-pixel
        // offsets (4.5, 6.795) → sub-pixel halo around the checkmark.
        '& .hd-checkbox-icon__glyph': {
          position: 'absolute',
          top: 7,
          left: 4,
        },

        // Hover state — 2px Darker-Blue stroke (chassis stays 24×24; stroke thickens via box-shadow).
        '&:hover .hd-checkbox-icon, &[data-force-hover] .hd-checkbox-icon': {
          boxShadow: `inset 0 0 0 2px ${componentTokens.checkbox.borderHover}`,
        },

        // Focus-visible — outer 28×28 ring via outline; inner chassis radius shrinks 4→3 per Figma.
        '&:focus-visible .hd-checkbox-icon, &[data-force-focus] .hd-checkbox-icon': {
          borderRadius: 3,
          outline: `1px solid ${componentTokens.checkbox.focusRing}`,
          outlineOffset: 2,
        },

        // Disabled — gray bg, no stroke (chassis fills with gray-3-5 per Figma).
        '&.Mui-disabled .hd-checkbox-icon': {
          backgroundColor: componentTokens.checkbox.bgDisabled,
          boxShadow: 'none',
          color: componentTokens.checkbox.checkFg,
        },

        // Error state — driven by the `data-error="true"` attribute projected
        // from the `error` prop (see mui-augment.d.ts + CheckboxExample).
        '&[data-error="true"] .hd-checkbox-icon': {
          boxShadow: `inset 0 0 0 2px ${componentTokens.checkbox.borderError}`,
        },
        '&[data-error="true"]:hover .hd-checkbox-icon': {
          boxShadow: `inset 0 0 0 2px ${componentTokens.checkbox.borderError}`,
        },
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // Radio — CATALOG
  // 10 variant cells from Figma matrix States {Default, Hover, Disabled, Error,
  // Focus} × On/Off {False, True}.
  //
  // MUI mapping:
  //   - Native `checked` prop carries Figma's On/Off axis (True ↔ checked).
  //   - Pseudo-classes carry the States axis: `:hover`, `:focus-visible`,
  //     `.Mui-disabled`, plus a custom `error` prop for Error state.
  //   - Custom icon (RadioIcon.tsx) replaces MUI's default radio glyphs via
  //     defaultProps; styling drives chassis (border, bg) + dot color.
  //
  // Figma source dimensions: 24×24 circular chassis (border-radius 14, fully
  // circular at 24px), 10×10 centered dot when checked. Focus state nests an
  // outer 28×28 ring (1px Teal-Accessible) outside the 24×24 chassis (inner
  // radius drops to 13 per Figma). CSS-equivalent: outline at +2 offset.
  // ────────────────────────────────────────────────────────────────────────────
  MuiRadio: {
    defaultProps: {
      disableRipple: true,
      icon: React.createElement(RadioIcon),
      checkedIcon: React.createElement(RadioIcon, { checked: true }),
    },
    styleOverrides: {
      root: {
        // MUI Radio wraps the icon in a button-like span with built-in
        // padding for the ripple. Reset to render exactly the 24×24 chassis +
        // 4px buffer for the focus-visible 28×28 ring offset.
        padding: 0,
        // Universal chassis styling lives on the .hd-radio-icon child span.
        // Border stroke painted via inset box-shadow so the 24×24 content area
        // is invariant to stroke weight (Figma stroke alignment behavior). A
        // CSS `border` would push the content-area inward and shift the dot
        // by the border width.
        '& .hd-radio-icon': {
          position: 'relative',
          width: 24,
          height: 24,
          borderRadius: 14,
          boxSizing: 'border-box',
          backgroundColor: componentTokens.radio.bgDefault,
          boxShadow: `inset 0 0 0 1px ${componentTokens.radio.borderDefault}`,
          color: componentTokens.radio.dotFg,
          display: 'inline-block',
          transition: 'box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background-color 120ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        // Centered 10×10 dot inside the 24×24 chassis at (x=7, y=7). Color
        // tracks `currentColor` so the chassis-level `color` token drives it
        // (defaults to dotFg; disabled cascade overrides to dotFgDisabled).
        '& .hd-radio-icon__dot': {
          position: 'absolute',
          top: 7,
          left: 7,
          width: 10,
          height: 10,
          borderRadius: 5,
          backgroundColor: 'currentColor',
        },

        // Hover state — 2px Teal-Accessible stroke (chassis stays 24×24; stroke thickens via box-shadow).
        '&:hover .hd-radio-icon, &[data-force-hover] .hd-radio-icon': {
          boxShadow: `inset 0 0 0 2px ${componentTokens.radio.borderHover}`,
        },

        // Focus-visible — outer 28×28 ring via outline; inner chassis radius shrinks 14→13 per Figma.
        '&:focus-visible .hd-radio-icon, &[data-force-focus] .hd-radio-icon': {
          borderRadius: 13,
          outline: `1px solid ${componentTokens.radio.focusRing}`,
          outlineOffset: 2,
        },

        // Disabled — gray bg, no stroke (chassis fills with gray-3-5 per Figma).
        // Dot inverts to gray-1 (dotFgDisabled).
        '&.Mui-disabled .hd-radio-icon': {
          backgroundColor: componentTokens.radio.bgDisabled,
          boxShadow: 'none',
          color: componentTokens.radio.dotFgDisabled,
        },

        // Error state — driven by the `data-error="true"` attribute projected
        // from the `error` prop (see mui-augment.d.ts + RadioExample).
        '&[data-error="true"] .hd-radio-icon': {
          boxShadow: `inset 0 0 0 2px ${componentTokens.radio.borderError}`,
        },
        '&[data-error="true"]:hover .hd-radio-icon': {
          boxShadow: `inset 0 0 0 2px ${componentTokens.radio.borderError}`,
        },
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // Toggle Button — CATALOG
  // Maps to MUI <Switch>. 8 cells (Type × Status = 2 × 4). Track + knob colors
  // and knob position vary by state via :hover / :focus-visible /.Mui-checked /
  // .Mui-disabled pseudo-selectors. `data-force-hover` and `data-force-focus`
  // mirror the interactive states at-rest for visual regression cells (Link/Checkbox
  // convention).
  //
  // MUI 5 Switch hardcodes its thumb (icon/checkedIcon props are declared but
  // not actually used inside the Switch JSX). So the X (Off) and Check (On)
  // glyphs are rendered via a single `.MuiSwitch-thumb::before` pseudo-element
  // with `mask-image` swapping between the two SVGs based on.Mui-checked.
  // currentColor drives the glyph's color via background-color: currentColor.
  // ────────────────────────────────────────────────────────────────────────────
  // MuiSwitch — constants hoisted to module scope (SWITCH_TK, SWITCH_THUMB_*).
  // Plain object literal here, matching the shape of MuiButton / MuiCheckbox / MuiAvatar.
  MuiSwitch: {
      defaultProps: {
        disableRipple: true,
        color: 'default' as const, // opt out of MUI's colorPrimary cascade — Heidrick owns all colors directly
      },
      styleOverrides: {
        root: {
          // Geometry: 68×36 track; thumb travels horizontally inside the 2px padding.
          width: 68,
          height: 36,
          padding: 0,
          overflow: 'visible', // outer focus ring + hover-shift translate render outside the box

          // SwitchBase — wraps the thumb, slides horizontally on.Mui-checked.
          // Knob travel math: track=68, thumb=32, padding=2 each side → 32px travel from x=2 to x=34.
          // data-force-hover / data-force-focus live on the.MuiSwitch-root (the prop forwards there);
          // we cascade them down to the switchBase via descendant selectors.
          '& .MuiSwitch-switchBase': {
            padding: 2,
            color: SWITCH_TK.iconOffResting, // currentColor for Off Default thumb glyph
            transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1), color 200ms cubic-bezier(0.4, 0, 0.2, 1)',

            // Suppress MUI's default ripple/focus background overlay
            '&:hover': {
              backgroundColor: 'transparent',
            },

            // Honor reduced-motion — instant snap, no transitions
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          },

          // ────────────────────────────────────────────────────────────────────
          // IMPORTANT: MUI Switch spreads extra props (data-force-*, etc.) to the
          // SwitchBase, NOT to the root span (verified 2026-05-19 from MUI 5.17
          // Switch.js source — `other` flows to SwitchSwitchBase, root only gets
          // className/sx/ownerState/children). So data-force-hover and
          // data-force-focus land on.MuiSwitch-switchBase, not.MuiSwitch-root.
          // Selectors below match that location.
          // ────────────────────────────────────────────────────────────────────

          // Off / Hover — knob nudges 4px RIGHT (mirror of On hover), icon stroke shifts to dark-blue
          '& .MuiSwitch-switchBase:not(.Mui-checked):not(.Mui-disabled):hover, & .MuiSwitch-switchBase[data-force-hover]:not(.Mui-checked):not(.Mui-disabled)': {
            transform: 'translateX(4px)',
            color: SWITCH_TK.iconOffActive,
          },
          // Off / Focus — icon stroke shifts to dark-blue (track border swap is on the track, below)
          '& .MuiSwitch-switchBase.Mui-focusVisible:not(.Mui-checked):not(.Mui-disabled), & .MuiSwitch-switchBase[data-force-focus]:not(.Mui-checked):not(.Mui-disabled)': {
            color: SWITCH_TK.iconOffActive,
          },

          // Checked (On) Default — knob at x=34 (translateX=32), icon stroke darker-blue
          '& .MuiSwitch-switchBase.Mui-checked': {
            transform: 'translateX(32px)',
            color: SWITCH_TK.iconOn,
          },
          // On / Hover — knob pulls back 4px LEFT (translateX=28); icon stays darker-blue
          '& .MuiSwitch-switchBase.Mui-checked:not(.Mui-disabled):hover, & .MuiSwitch-switchBase.Mui-checked[data-force-hover]:not(.Mui-disabled)': {
            transform: 'translateX(28px)',
            color: SWITCH_TK.iconOn,
          },
          // On / Focus — knob stays at translateX=32; outer ring on root handled below.

          // Disabled — icon color muted, knob position fixed at the resting Off/On position
          '& .MuiSwitch-switchBase.Mui-disabled': {
            color: SWITCH_TK.iconDisabled,
          },

          // Thumb — 32×32 white ellipse; glyph rendered via ::before pseudo with mask-image.
          '& .MuiSwitch-thumb': {
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: SWITCH_TK.knobBg,
            boxShadow: 'none', // MUI default has elevation; Heidrick is flat
            position: 'relative',
            transition: 'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',

            '&::before': {
              content: '""',
              position: 'absolute',
              top: 8,
              left: 8,
              width: 16,
              height: 16,
              backgroundColor: 'currentColor', // inherits switchBase's color
              maskImage: SWITCH_THUMB_OFF_MASK,
              WebkitMaskImage: SWITCH_THUMB_OFF_MASK,
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
              maskSize: '16px 16px',
              WebkitMaskSize: '16px 16px',
              transition: 'mask-image 0ms', // instant glyph swap; color animates separately
            },

            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          },

          // On — swap thumb glyph mask to checkmark
          '& .Mui-checked .MuiSwitch-thumb::before': {
            maskImage: SWITCH_THUMB_ON_MASK,
            WebkitMaskImage: SWITCH_THUMB_ON_MASK,
          },

          // Disabled — knob fill greys out (both Off and On)
          '& .Mui-disabled .MuiSwitch-thumb': {
            backgroundColor: SWITCH_TK.knobDisabledBg,
          },

          // Track — the 68×36 pill behind the knob. Selectors mirror MUI's internal patterns
          // (.MuiSwitch-switchBase.Mui-checked +.MuiSwitch-track sibling combinator) so our
          // overrides win on specificity equality + source order.
          '& .MuiSwitch-track': {
            width: '100%',
            height: '100%',
            borderRadius: 40,
            opacity: 1, // MUI defaults to 0.38 — we drive contrast via real fills, not alpha
            boxSizing: 'border-box',
            backgroundColor: SWITCH_TK.trackOffBg,
            border: `1px solid ${SWITCH_TK.trackBorderResting}`,
            transition:
              'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',

            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          },

          // On — track becomes navy with navy border. Sibling selector matches MUI's pattern;
          // opacity reaffirmed because MUI sets opacity: 0.5 on.Mui-checked +.MuiSwitch-track.
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: SWITCH_TK.trackOnBg,
            borderColor: SWITCH_TK.trackBorderOn,
            opacity: 1,
          },

          // Off / Focus — track border swaps to darker-blue (focus indicator is built INTO the track for Off).
          // Both real :focus-visible and data-force-focus land on the switchBase (per MUI 5.17 Switch.js).
          '& .MuiSwitch-switchBase.Mui-focusVisible:not(.Mui-checked) + .MuiSwitch-track, & .MuiSwitch-switchBase[data-force-focus]:not(.Mui-checked) + .MuiSwitch-track': {
            borderColor: SWITCH_TK.trackBorderFocusOff,
          },

          // Focus outer ring (BOTH Off and On). Figma's COMPONENT_SET ships the same 72×40 outer
          // rectangle at offset (−2,−2) with 1px darker-blue stroke INSIDE around BOTH variants — the
          // initial JSON probe only surfaced the explicit Rectangle 1 child for On/Focus, but the
          // exported PNG for Off/Focus also has the outer ring (verified pixel-sampling 2026-05-19,
          // see CSS equivalent: outline at offset 1px (inner
          // edge of outline lands 1px outside the 68×36 root → visible stroke at y=-1 to y=-2,
          // matching Figma's 1px-inside-72×40-at-offset-(-2,-2) pixels). Trigger lives on the
          // switchBase (where MUI puts both.Mui-* state classes AND the data-force-* attribute);
          // :has() lifts the rule to the root so the outline renders on the outer span.
          '&:has(.MuiSwitch-switchBase.Mui-focusVisible), &:has(.MuiSwitch-switchBase[data-force-focus])': {
            outline: `1px solid ${SWITCH_TK.focusRingOn}`,
            outlineOffset: 1,
            borderRadius: 40, // outline follows the box border-radius (the track is 40, root takes same)
          },

          // Disabled — both Off and On collapse to gray-5 fill with gray-1 border. Sibling-selector
          // form matches MUI's own.Mui-disabled +.MuiSwitch-track pattern.
          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
            backgroundColor: SWITCH_TK.trackDisabledBg,
            borderColor: SWITCH_TK.trackBorderResting,
            opacity: 1,
          },

          // ────────────────────────────────────────────────────────────────────
          // Small size (size="small") — 48×24 track, 20×20 knob, 12×12 glyph.
          // A pure geometric scale of the default: all colors, borders, focus
          // ring, disabled treatment, and the Off/Hover 4px knob-shift inherit
          // from the base rules above. Only the box dimensions, the On-position
          // knob travel, and the thumb/glyph sizes change.
          // Knob travel math: track=48, thumb=20, padding=2 each side → 24px
          // travel from x=2 to x=26. On/Hover pulls back 4px (translateX=20),
          // mirroring the default size's 4px hover-shift.
          // ────────────────────────────────────────────────────────────────────
          '&.MuiSwitch-sizeSmall': {
            width: 48,
            height: 24,

            // On / Default — knob at x=26 (translateX=24)
            '& .MuiSwitch-switchBase.Mui-checked': {
              transform: 'translateX(24px)',
            },
            // On / Hover — knob pulls back 4px LEFT (translateX=20)
            '& .MuiSwitch-switchBase.Mui-checked:not(.Mui-disabled):hover, & .MuiSwitch-switchBase.Mui-checked[data-force-hover]:not(.Mui-disabled)': {
              transform: 'translateX(20px)',
            },

            // Thumb — 20×20 with a 12×12 glyph centered ((20−12)/2 = 4px inset)
            '& .MuiSwitch-thumb': {
              width: 20,
              height: 20,

              '&::before': {
                top: 4,
                left: 4,
                width: 12,
                height: 12,
                maskSize: '12px 12px',
                WebkitMaskSize: '12px 12px',
              },
            },
          },
        },
      },
  },
};
